import React, { useEffect, useState } from "react";
import api from "../../services/api";
import profileService from "../../services/profileService";
import userSourceScopeService from "../../services/userSourceScopeService";
import SourceScopeEditor from "./SourceScopeEditor";
import LoadingSpinner from "../common/LoadingSpinner";
import { formatInUserTimezone } from "../../utils/timezone";

const AVAILABLE_SOURCES = ["Chameleon", "FavorDrop", "DFC", "None"];

function PendingUsers() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await profileService.listPending();
      setUsers(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load pending users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openApprove = (user) =>
    setDialog({
      user,
      role: "SR",
      scopeMode: "all",
      scopeSources: [],
    });

  const confirmApprove = async () => {
    if (!dialog) return;
    setSaving(true);
    setError("");
    try {
      await profileService.approveUser(dialog.user.user_id, {
        role: dialog.role,
      });

      /* R2 — set source scope immediately after approval */
      const scopePayload =
        dialog.scopeMode === "all"
          ? { mode: "all" }
          : dialog.scopeMode === "none"
            ? { mode: "none" }
            : { mode: "specific", sources: dialog.scopeSources };

      await userSourceScopeService.set(dialog.user.user_id, scopePayload);

      setDialog(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to approve user");
    } finally {
      setSaving(false);
    }
  };

  const reject = async (user) => {
    if (!window.confirm(`Reject registration for ${user.email}?`)) return;
    setBusyId(user.user_id);
    try {
      await profileService.rejectUser(user.user_id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to reject user");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingSpinner message="Loading pending users..." />;

  return (
    <div className="pending-users">
      <div className="management-header">
        <div>
          <h2>Pending Users</h2>
          <p className="text-muted">
            New registrations awaiting admin approval.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={load}>
          <i className="fas fa-sync-alt" /> Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {users.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-user-check" />
          <h3>No pending users</h3>
          <p>All registrations are up to date.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id}>
                  <td>
                    <strong>{u.full_name || "—"}</strong>
                  </td>
                  <td>{u.email}</td>
                  <td>{u.company_name || "—"}</td>
                  <td>
                    {u.approval_requested_at
                      ? formatInUserTimezone(u.approval_requested_at)
                      : "—"}
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => openApprove(u)}
                      disabled={busyId === u.user_id}
                    >
                      <i className="fas fa-check" /> Approve
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => reject(u)}
                      disabled={busyId === u.user_id}
                    >
                      <i className="fas fa-times" /> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <div
          className="modal-overlay"
          onMouseDown={() => !saving && setDialog(null)}
        >
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Approve {dialog.user.email}</h2>
            <p className="text-muted">
              Set the role and lead source access this user will get.
            </p>

            <div className="form-group">
              <label>Role</label>
              <select
                className="form-control"
                value={dialog.role}
                onChange={(e) =>
                  setDialog((d) => ({ ...d, role: e.target.value }))
                }
                disabled={saving}
              >
                <option value="SR">Sales Representative (SR)</option>
                <option value="SM">Sales Manager (SM)</option>
                <option value="Admin">Administrator</option>
              </select>
            </div>

            <div className="form-group">
              <label>Lead Source Scope</label>
              <SourceScopeEditor
                mode={dialog.scopeMode}
                sources={dialog.scopeSources}
                available={AVAILABLE_SOURCES}
                onChange={({ mode, sources }) =>
                  setDialog((d) => ({
                    ...d,
                    scopeMode: mode,
                    scopeSources: sources,
                  }))
                }
                disabled={saving}
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDialog(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmApprove}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Approving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" /> Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingUsers;